<template>
    <div class="mb-4 rounded-md bg-white shadow" :class="{ 'opacity-50': menu.archived }">
        <div>
            <div
                class="flex flex-wrap items-center justify-between border-b border-gray-200 px-4 py-5 shadow-sm sm:flex-nowrap sm:px-6"
            >
                <div>
                    <h3
                        class="font-heading text-base font-medium flex items-center leading-6 text-gray-900"
                    >
                        <span
                            @click="
                                $router.push({
                                    name: 'editMenu',
                                    params: { id: menu.id }
                                })
                            "
                            class="text-indigo-600 cursor-pointer">
                            {{ menu.name }}
                        </span>
                        <venue-name-tag
                          class="hidden sm:inline-block ml-3 bg-gray-50"
                            v-if="menu.venue && menu.venue.name"
                            :name="menu.venue.adminName || menu.venue.name"
                            :truncate-length="25"
                        />
                    </h3>
                    <span v-if="menu.lastSyncedAt" class="text-xs"
                        >Synced:
                        {{ menu.lastSyncedAt | moment('from', 'now') }}
                    </span>

                    <p class="mt-1 max-w-2xl text-xs leading-5">
                        Created: {{ menu.created_at | moment('LL') }}
                    </p>
                </div>
                <div>
                    <div
                        v-if="isFeatureAvailable(FeaturesEnum.MULTI_MENU)"
                        class="inline-flex items-center"
                    >
                        <span class="mr-3 text-sm leading-5 text-gray-500">
                            Toggle active / inactive
                        </span>
                        <toggle-switch
                            v-model="menu.active"
                            @input="updateMenuStatus(menu.id, menu.active)"
                        />
                    </div>

                    <div
                        class="mt-2 flex flex-shrink-0 flex-col items-center sm:ml-4"
                    >
                        <base-button
                            buttonText="Edit menu"
                            size="sm"
                            @clicked="
                                $router.push({
                                    name: 'editMenu',
                                    params: { id: menu.id }
                                })
                            "
                        />
                    </div>
                    <div
                        v-if="
                            menu?.venue?.posProvider === 'ikentoo' && menu.posId
                        "
                        class="mt-2 flex flex-shrink-0 flex-col items-center sm:ml-4"
                    >
                        <base-button
                            size="sm"
                            :isLoading="isPosLoading"
                            :disabled="isPosLoading"
                            @clicked="syncMenuWithLightspeed"
                            button-text="Sync menu"
                        >
                            <template #left-icon>
                                <base-lightspeed-icon />
                            </template>
                        </base-button>
                    </div>
                    <div
                        v-if="menu?.venue?.integrations?.zonal?.enabled"
                        class="mt-2 flex flex-shrink-0 flex-col items-center sm:ml-4"
                    >
                        <base-button
                            size="sm"
                            :isLoading="isPosLoading"
                            :disabled="isPosLoading"
                            @clicked="syncMenuWithZonal"
                            button-text="Sync menu"
                        >
                            <template #left-icon>
                                <base-zonal-icon />
                            </template>
                        </base-button>
                    </div>

                </div>
            </div>
            <div
                class="grid grid-cols-1 gap-5 rounded-b-md bg-gray-50 px-4 py-3 text-xs text-gray-600 shadow-inner sm:grid-cols-5 sm:px-6"
                v-if="menu.statistics && menu.statistics.productsCount > 0"
            >
                <div class="flex flex-col">
                    <span class="">
                        {{ menu.statistics.productsCount }} menu items
                    </span>
                    <span class="">
                        {{ menu.statistics.categoriesCount }} categories
                    </span>
                </div>
                <div class="ml-0 flex flex-col sm:ml-4">
                    <span
                        v-tooltip="
                            menu.statistics.productsCount -
                            menu.statistics.missingImage +
                            '/' +
                            menu.statistics.productsCount +
                            ' items have images. Adding images to products increases revenue up to 30%.'
                        "
                    >
                        Item images
                    </span>
                    <div
                        class="mt-1 h-2 w-full rounded-full bg-gray-200 shadow-inner"
                    >
                        <div
                            class="h-2 rounded-full"
                            :class="
                                progressBarColour(
                                    menu.statistics.imageCompletePercentage
                                )
                            "
                            :style="
                                'width: ' +
                                menu.statistics.imageCompletePercentage +
                                '%'
                            "
                        ></div>
                    </div>
                </div>
                <div class="ml-0 flex flex-col sm:ml-4">
                    <span
                        v-tooltip="
                            menu.statistics.productsCount -
                            menu.statistics.missingDescription +
                            '/' +
                            menu.statistics.productsCount +
                            ' items have descriptions. Add descriptions to each menu item to help guests.'
                        "
                    >
                        Item descriptions
                    </span>
                    <div
                        class="mt-1 h-2 w-full rounded-full bg-gray-200 shadow-inner"
                    >
                        <div
                            class="h-2 rounded-full"
                            :class="
                                progressBarColour(
                                    menu.statistics
                                        .descriptionCompletePercentage
                                )
                            "
                            :style="
                                'width: ' +
                                menu.statistics.descriptionCompletePercentage +
                                '%'
                            "
                        ></div>
                    </div>
                </div>
                <div class="ml-0 flex flex-col sm:ml-4">
                    <span
                        v-tooltip="
                            menu.statistics.productsCount -
                            menu.statistics.missingTags +
                            '/' +
                            menu.statistics.productsCount +
                            ' items have dietary or allergen tags. Add allergen tags for compliance and dietary tags to help guests filter dishes.'
                        "
                    >
                        Diets & allergens
                    </span>
                    <div
                        class="mt-1 h-2 w-full rounded-full bg-gray-200 shadow-inner"
                    >
                        <div
                            class="h-2 rounded-full"
                            :class="
                                progressBarColour(
                                    menu.statistics.tagCompletePercentage
                                )
                            "
                            :style="
                                'width: ' +
                                menu.statistics.tagCompletePercentage +
                                '%'
                            "
                        ></div>
                    </div>
                </div>
                <div class="ml-0 flex flex-col sm:ml-4">
                    <span
                        v-tooltip="
                            menu.statistics.productsCount -
                            menu.statistics.itemsUnavailable +
                            '/' +
                            menu.statistics.productsCount +
                            ' items are currently available to purchase'
                        "
                    >
                        Availability
                    </span>
                    <div
                        class="mt-1 h-2 w-full rounded-full bg-gray-200 shadow-inner"
                    >
                        <div
                            class="h-2 rounded-full"
                            :class="
                                progressBarColour(
                                    menu.statistics.itemsAvailablePercentage
                                )
                            "
                            :style="
                                'width: ' +
                                menu.statistics.itemsAvailablePercentage +
                                '%'
                            "
                        ></div>
                    </div>
                </div>
            </div>
            <div
                class="px-4 py-5 sm:px-6"
                v-if="menu.syndication && menu.syndication.length"
            >
                <base-opentable-logo
                    class="h-auto w-20"
                    v-tooltip="'Published on Open Table'"
                />
            </div>
        </div>
    </div>
</template>

<script>
import Ably from 'ably';
import { mapGetters } from 'vuex';
import { FeaturesEnum } from '@/enums';
import ToggleSwitch from '@/components/formComponents/ToggleSwitch';
import VenueNameTag from "@/components/common/VenueNameTag";

export default {
    name: 'MenuListItem',
    components: {
        VenueNameTag,
        ToggleSwitch
    },
    props: {
        menu: {
            type: Object,
            required: true
        },
        index: {
            type: Number,
            required: true
        }
    },
    computed: {
        ...mapGetters({ isFeatureAvailable: 'user/isFeatureAvailable' }),
        isPosManaged() {
            return !!this.menu.deliverectId;
        }
    },
    data() {
        return {
            FeaturesEnum,
            isPosLoading: false,
            ably: null,
            syncProgress: 0,
            syncStatus: 'Not started',
            isError: false,
            channel: null,
            syncNotificationId: null
        };
    },
    methods: {
        async updateMenuStatus(id, active) {
            try {
                await this.$axios.patch(`/menus/${this.menu.id}`, { active });
            } catch (error) {
                throw new Error(`API ${error}`);
            }
        },
        progressBarColour(percent) {
            if (percent < 50) {
                return 'bg-status-low';
            } else if (percent < 75) {
                return 'bg-status-mid';
            } else {
                return 'bg-status-success';
            }
        },
        async syncMenuWithLightspeed() {
            if (this.isPosLoading) {
                return;
            }

            this.isPosLoading = true;

            this.syncStatus = 'Starting sync';
            this.syncProgress = 0;
            this.isError = false;

            this.syncNotificationId = this.$notify({
                group: 'settings',
                type: 'info',
                duration: -1, // Persistent until manually closed
                text: 'Lightspeed menu sync started (0%)'
            });

            try {
                const { data } = await this.$axios.post(
                    `/ikentoo/update-menu`,
                    {
                        menuId: this.menu.id,
                        venueId: this.menu.venueId
                    }
                );

                if (data && data.success) {
                    this.syncStatus = 'Sync initiated';
                    this.subscribeToProgress(data.channelName, data.eventName);
                } else {
                    throw new Error('Sync initialization failed');
                }
            } catch (e) {
                throw new Error(`API ${e}`);
            } finally {
                this.isPosLoading = false;
            }
        },
        async syncMenuWithZonal() {
            if (this.isPosLoading) {
                return;
            }

            this.isPosLoading = true;

            this.syncStatus = 'Starting sync';
            this.syncProgress = 0;
            this.isError = false;

            this.syncNotificationId = this.$notify({
                group: 'settings',
                type: 'info',
                duration: -1,
                text: 'Zonal menu sync started (0%)'
            });

            try {
                const { data } = await this.$axios.post(
                    `/zonal/update-menu`,
                    {
                        menuId: this.menu.id,
                        venueId: this.menu.venueId
                    }
                );

                if (data && data.success) {
                    this.syncStatus = 'Sync initiated';
                    this.subscribeToProgress(data.channelName, data.eventName);
                } else {
                    throw new Error('Sync initialization failed');
                }
            } catch (e) {
                throw new Error(`API ${e}`);
            } finally {
                this.isPosLoading = false;
            }
        },

        subscribeToProgress(channelName, eventName) {
            // Initialize Ably client (use API key from environment or config)
            this.ably = new Ably.Realtime({
                key: process.env.VUE_APP_ABLY_API_KEY
            });

            // Get the channel
            this.channel = this.ably.channels.get(channelName);

            // Subscribe to the event
            this.channel.subscribe(eventName, message => {
                const {
                    percent,
                    currentStep,
                    error
                } = message.data.progressData;

                this.syncProgress = percent;
                this.syncStatus = currentStep;
                this.isError = error || false;

                // Close the existing notification
                this.$notify.close(this.syncNotificationId);

                // Update notification with progress
                if (this.isError) {
                    // Show error notification
                    this.syncNotificationId = this.$notify({
                        group: 'settings',
                        type: 'error',
                        duration: 5000,
                        text: `Sync error: ${currentStep}`
                    });
                } else {
                    // Show progress notification
                    this.syncNotificationId = this.$notify({
                        group: 'settings',
                        type: 'info',
                        duration: -1, // Persistent until manually closed
                        text: `Sync progress: ${currentStep} (${percent}%)`
                    });
                }

                // If sync is complete, clean up the subscription and refresh data
                if (percent === 100 || error) {
                    this.unsubscribe();
                    this.isPosLoading = false;
                }
            });
        },
        unsubscribe() {
            if (this.channel) {
                this.channel.unsubscribe();
                this.channel = null;
            }

            if (this.ably) {
                this.ably.close();
                this.ably = null;
            }
        }
    }
};
</script>

<style>
.bg-status-success {
    background-color: #74b382;
}
.bg-status-mid {
    background-color: #e1d346;
}
.bg-status-low {
    background-color: #f3a746;
}
</style>
